/* preset values for tooltip width */
const TOOLTIP_WIDTH = {
    SMALL: 'ah-tt-sm',
    MEDIUM: 'ah-tt-md',
    LARGE: 'ah-tt-lg',
    XLARGE: 'ah-tt-xl',
    XXLARGE: 'ah-tt-xxl'
};

/* direction of the tooltip pointer */
const TOOLTIP_POINTER_DIRECTION = {
    LEFT: 'point-left',
    RIGHT: 'point-right',
    BOTTOM: 'bottom-point',
    TOP: 'top-point'
};

/**
 * Class to handle displaying accessible and UX-consistent tooltips. In order to construct a tooltip, add a SPAN or a DIV container to hold
 * your triggering button and tooltip, and then instantiate this class, passing in the required params. You may need to refine the position
 * of the info icon by setting positioning style attributes on your container (see example below). The info icon button should typically
 * be positioned just to the right of the label/form field it is describing.
 *
 * EXAMPLE USAGE:
 *
 * $(document).ready(function () {
        let tooltipConfig = {
            tooltipText: '<p><strong>Method 1:</strong> Merge as-is.</p><p><strong>Method 2:</strong> Discard the source and retain the target record data in case of conflict. In this method, GuidingCare shall always consider the target record’s data as the highest priority in case of conflict. E.g., If the care plan record in source and target member has overlapping dates, then the record in target shall be available in merged record & the record in source shall be discarded.</p>',
            width: TOOLTIP_WIDTH.LARGE,
            tooltipHeader: 'How Conflicts are Resolved'
        }
        new A11yTooltipInstance('conflictResolutionTooltip', 'how conflicts are resolved tooltip', tooltipConfig);

 * <span id="conflictResolutionTooltip" style="position:relative; top: -4px;"></span>
 *
 * CAUTION:
 *      - make sure you check the keyboard navigation for your tooltip (you should be able to tab to the tooltip button and hit the Enter key
 *        to toggle the tooltip) - verify that hitting the Enter key does not invoke some other action on the page (e.g. Search, form Submit, etc.)
 *
 * POSSIBLE ENHANCEMENTS:
 *      - add support for "complex" tooltips (perhaps with tooltip header, text formatting, close button)
 *      - dynamic sizing
 *      - dynamic pointer direction
 *
 * @param {string} containerId ID of the <span> or <div> container that will hold the tooltip
 * @param {string} tooltipText text to add inside the tooltip
 * @param {string} srText required screen reader text to add to the tooltip button; this will be announced by the screen reader when a keyboard user focuses on the tooltip button
 * @param {object} tooltipConfig configuration object for the tooltip contents
 *                  tooltipConfig.tooltipText text to add inside the tooltip
 *                  [tooltipConfig.width=TOOLTIP_WIDTH.MEDIUM] optional width of the tooltip (valid values are from TOOLTIP_WIDTH constant)
 *                  [tooltipConfig.pointerDirection=TOOLTIP_POINTER_DIRECTION.LEFT] optional direction of the tooltip pointer (valid values are from TOOLTIP_POINTER_DIRECTION constant)
 *                  [tooltipConfig.tooltipHeader] optional text to add for a tooltip header (used for more "complex" tooltips only)
 *                  [tooltipConfig.tooltipIcon] optional icon class to use for the trigger icon (e.g. "far fa-sliders-h") - default is the info icon
 *                  [tooltipConfig.triggerButtonClass] optional CSS class to add to the tooltip trigger button
 *                  [tooltipConfig.fetchContentCallback] optional name of callback function to use for dynamic population of the tooltip content. This callback must
 *                                                       return a properly-formatted HTML string (e.g. search for "getMyMembersFilterCriteriaHtml" in the code)
 */
let A11yTooltipInstance = class A11yTooltipClass {

    #containerId;   // the ID of the container where the tooltip will be placed
    #tooltipConfig;
    #srText;
    #tooltipText;
    #width;
    #pointerDirection;
    #tooltipHeader;
    #tooltipIcon;
    #triggerButtonClass;
    #fetchContentCallback;

    #container;     // the container where the tooltip will be placed
    #trigger;
    #tooltip;
    #closeButton;
    #parentGrid;
    #isGridTooltip = false;

    constructor(containerId, srText, tooltipConfig) {
        this.#containerId = containerId;
        this.#srText = srText;
        this.#tooltipText = tooltipConfig.tooltipText;
        this.#width = tooltipConfig.width;
        this.#pointerDirection = tooltipConfig.pointerDirection;
        this.#tooltipHeader = tooltipConfig.tooltipHeader ? tooltipConfig.tooltipHeader : '';
        this.#tooltipIcon = tooltipConfig.tooltipIcon;
        this.#triggerButtonClass = tooltipConfig.triggerButtonClass;
        this.#fetchContentCallback = tooltipConfig.fetchContentCallback;

        if (!srText) {
            DevUtils.logWarn('SR-ONLY text not passed in for tooltip [' + this.#containerId + ']. The trigger button of a tooltip is required to have sr-only text for a11y.', 'A11yTooltipInstance.constructor()');
        }

        // logic to determine if the current tooltip is inside a grid (so we can do special things to it, if it is)
        let grid = $('#' + containerId).closest('.k-grid');
        if (grid && $(grid).length > 0) {
            this.#isGridTooltip = $.contains(grid[0], document.getElementById(containerId));
            if (this.#isGridTooltip) {
                this.#parentGrid = grid;
            }
        }

        this.#initTooltip();
    }

    /**
     * Function to refresh the tooltip content using the callback function set via tooltipConfig.fetchContentCallback
     */
    refreshTooltipContent() {
        this.#fetchTooltipContent();
    }

    /////////////////////
    // private methods //
    /////////////////////
    #initTooltip() {
        let _this = this;
        this.#container = $('#' + this.#containerId);

        if (!this.#container || this.#container.length === 0) {
            DevUtils.logWarn('Cannot find tooltip container for containerId[' + this.#containerId + ']', 'A11yTooltipInstance.initTooltip()');
            return;
        }
        this.#width = this.#width ? this.#width : TOOLTIP_WIDTH.MEDIUM;
        this.#pointerDirection = this.#pointerDirection ? this.#pointerDirection : TOOLTIP_POINTER_DIRECTION.LEFT;
        let containerClass;

        if (this.#isGridTooltip) {
            containerClass = 'a11y-tooltip-container ah-grid-tooltip';
            this.#tooltipIcon = this.#tooltipIcon ? 'ah-tooltip-icon ' + this.#tooltipIcon : 'ah-tooltip-icon fas fa-info';
        } else {
            containerClass = 'a11y-tooltip-container';
            this.#tooltipIcon = this.#tooltipIcon ? 'ah-tooltip-icon ' + this.#tooltipIcon : 'ah-tooltip-icon far fa-info-circle';
        }

        let tooltip =
            '<div id="' + this.#containerId + '_container" class="' + containerClass + '">' +
            '   <button type="button" class="a11y-tooltip-trigger ' + this.#triggerButtonClass + '" aria-controls="' + this.#containerId + '_tooltip" aria-expanded="false">' +
            '       <span class="' + this.#tooltipIcon + '" aria-hidden="true"></span>' +
            '       <span class="sr-only">' + this.#srText + '</span>' +
            '   </button>' +
            '   <div id="' + this.#containerId + '_tooltip" class="zindex9 a11y-tooltip ' + this.#pointerDirection + ' ' + this.#width + '" role="tooltip" aria-live="polite">' +
            '       <h2 class="a11y-tooltip-header">' + this.#tooltipHeader + '</h2>' +
            '       <button type="button" id="' + this.#containerId + '_closeButton" class="a11y-tooltip-close-btn" aria-label="close"><i class="far fa-times" aria-hidden="true"></i></button>' +
            '       <div id="' + this.#containerId + '_tooltipContent" class="a11y-tooltip-content">' + this.#tooltipText + '</div>' +
            '   </div>' +
            '</div >';

        $(this.#container).append(tooltip);

        if (this.#tooltipHeader) {
            $(this.#container).find('.a11y-tooltip-header').css('display', 'block');
        }

        $(this.#container).find('.a11y-tooltip-close-btn').css('display', 'block');

        this.#trigger = document.querySelector('#' + this.#containerId + '_container .a11y-tooltip-trigger');
        this.#tooltip = document.querySelector('#' + this.#containerId + '_tooltip');
        this.#closeButton = document.querySelector('#' + this.#containerId + '_closeButton');

        this.#trigger.addEventListener('click', function (e) {
            _this.#toggleTooltip(e,e.currentTarget);
        });

        this.#trigger.addEventListener('keyup', function (e) {
            _this.#handleTriggerKeyup(e);
        });

        this.#closeButton.addEventListener('click', function (e) {
            _this.#hideTooltip(e);
            _this.#trigger.focus();
        });

        this.#trigger.addEventListener('keypress', function (e) {
            // this is here to prevent screens from implementing an overriding keypress event listener which might do something else (e.g. invoke a search button)
            e.stopImmediatePropagation();
        });
    }

    #showTooltip(e) {
        $(document).find('.a11y-tooltip.is-visible').each(function (_i, e) {
            var _trigger = $("#" + e.id).siblings(".a11y-tooltip-trigger.undefined");
            _trigger.attr("aria-expanded", "false");
            $("#" + e.id).css('display', 'none');
            $("#" + e.id).removeClass('is-visible');
        });

        this.#fetchTooltipContent();

        this.#trigger.setAttribute('aria-expanded', true);
        this.#tooltip.classList.add('is-visible');
        $(this.#tooltip).css('display', 'block');

        if (this.#isGridTooltip) {
            this.#calculateTooltipPositionInGrid(this.#trigger);

            // temporarily remove the overflow-y:hidden on the grid so the tooltip can display outside of it if necessary
            $(this.#parentGrid).addClass('ah-grid-overflow-unset');

            let _this = this;

            // if the *grid* is scrolled, hide the tooltip so we don't have an issue where the tooltip can run above the grid
            $(this.#parentGrid).find('.a11y-k-grid-container').off('scroll').scroll(function (e) {
                _this.#hideTooltip(e);
            });
            $(this.#parentGrid).closest('.a11y-k-grid-container').off('scroll').scroll(function (e) {
                _this.#hideTooltip(e);
            });
            // if the *browser window* is scrolled, recalculate the position of the tooltip so it "sticks" to the tooltip button; this
            // will allow users to see the entire tooltip in case it is large and runs off the bottom of the screen
            $(window).off('scroll').scroll(function (e) {
                _this.#calculateTooltipPositionInGrid(_this.#trigger);
            });
        }

        e.stopImmediatePropagation();
    }

    #hideTooltip(e) {
        this.#trigger.setAttribute('aria-expanded', false);
        this.#tooltip.classList.remove('is-visible');
        $(this.#tooltip).css('display', 'none');

        $(this.#parentGrid).removeClass('ah-grid-overflow-unset');

        e.stopImmediatePropagation();
    }

    #toggleTooltip(e,t) {
        const ariaExpanded = $(t).attr("aria-expanded");
        if (ariaExpanded === 'true') {
            this.#hideTooltip(e);
        } else {
            this.#showTooltip(e);
        }
    }

    #handleTriggerKeyup(e) {
        if (e.key === 'Escape') {
            this.#hideTooltip(e);
        }
    }

    #fetchTooltipContent() {
        if (this.#fetchContentCallback) {
            let tooltipText = this.#fetchContentCallback();
            $(this.#container).find('.a11y-tooltip-content').html(tooltipText);
        }
    }

    /**
     *  When a tooltip is in a grid, we have to turn off the relative positioning of the tooltip button so that it doesn't encroach into the
     *  grid column header when scrolled. When we do this, it breaks the automatic positioning of the tooltip relative to its button, so
     *  this function serves to manually calculate the tooltip position based on its absolute positioning on the screen
     * @param {any} tooltipButtonElem
     */
    #calculateTooltipPositionInGrid(tooltipButtonElem) {
        // this is the absolute position of the tooltip on the screen
        let tooltipBtnRect = tooltipButtonElem.getBoundingClientRect();

        // set the absolute tooltip position relative to the entire viewport
        let left = 0;
        let top = tooltipBtnRect.top - 5;

        if (this.#pointerDirection === TOOLTIP_POINTER_DIRECTION.RIGHT) {
            let tooltipWidth = $(this.#container).find('.a11y-tooltip').outerWidth();
            left = tooltipBtnRect.left - tooltipWidth - 5;
        } else {
            left = tooltipBtnRect.left + tooltipButtonElem.offsetWidth + 5;
        }

        DevUtils.logDebug('A11yTooltipInstance.getTooltipPositionInGrid()', null, { tooltipBtnRect: tooltipBtnRect, _left: left, _top: top });

        // now set the position
        this.#tooltip.style.left = left + 'px';
        this.#tooltip.style.top = top + 'px';
    }
};
